/**
 * Keeping the game running while the window is hidden.
 *
 * Every animation on the client is driven by a Pixi ticker, and a Pixi
 * ticker is driven by `requestAnimationFrame`. The browser stops rAF
 * completely for a minimised window or a background tab, while the round
 * pacing in the store keeps ticking on `setTimeout`. The result the player
 * sees is the bug this module exists for: the board freezes mid-round, the
 * logic and the sound run ahead of it, and everything the frozen frames
 * owed lands in one burst the moment the window comes back.
 *
 * So while the page is hidden we drive the tickers ourselves.
 *
 * Two details make it actually work:
 *
 *   - The clock is a Web Worker (`public/background-clock.js`).
 *     `setInterval` on a hidden page is clamped to one second per tick
 *     (and less often still after a few minutes), a worker's timer is not.
 *     If the worker cannot start, a plain timer takes over.
 *   - Time is fed to the tickers in small steps rather than one jump.
 *     Pixi clamps a single frame to `maxElapsedMS`, so handing it a
 *     one-second gap would advance the game by 100 ms and the round would
 *     crawl in slow motion. Stepping keeps background time real time.
 */

import type { Application } from 'pixi.js'
import { log } from '../log'

/** Virtual frame length fed to the tickers while hidden, ms. */
const STEP_MS = 50
/** Worker tick period. One step per tick when the timer is not throttled. */
const TICK_MS = 50
/**
 * Most catch-up one tick may do, in steps. A long-suspended laptop comes
 * back with minutes of unfed time; replaying it would lock the main thread
 * for the whole of it. Two seconds is enough to cover timer throttling and
 * short enough to stay invisible.
 */
const MAX_STEPS = 40

const apps = new Set<Application>()

let clockStop: (() => void) | null = null
/** Timestamp the tickers have already been driven up to, ms. */
let fedUntil = 0

/**
 * Drive this app's ticker while the page is hidden.
 *
 * Returns an unregister function — call it when the app is destroyed, or
 * the clock would keep poking a dead renderer.
 */
export function driveWhileHidden(app: Application): () => void {
  apps.add(app)
  ensureVisibilityHook()
  if (document.hidden) startClock()
  return () => {
    apps.delete(app)
    if (apps.size === 0) stopClock()
  }
}

let hooked = false

function ensureVisibilityHook(): void {
  if (hooked) return
  hooked = true
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) startClock()
    else stopClock()
  })
}

function startClock(): void {
  if (clockStop || apps.size === 0) return
  fedUntil = performance.now()
  clockStop = spawnClock(tick)
}

function stopClock(): void {
  clockStop?.()
  clockStop = null
}

/** Advance every registered ticker up to now, in fixed steps. */
function tick(): void {
  // A late tick after the window came back must not double-drive: rAF is
  // already running the tickers again.
  if (!document.hidden) return
  const now = performance.now()
  let steps = Math.floor((now - fedUntil) / STEP_MS)
  if (steps <= 0) return
  if (steps > MAX_STEPS) {
    // Drop the excess instead of replaying it: the player was away, and
    // the round they come back to should be the current one.
    fedUntil = now - MAX_STEPS * STEP_MS
    steps = MAX_STEPS
  }
  for (let i = 0; i < steps; i++) {
    fedUntil += STEP_MS
    for (const app of apps) {
      try {
        if (app.ticker.started) app.ticker.update(fedUntil)
      } catch {
        // A destroyed app that never unregistered. Drop it and move on:
        // background animation must not take the page down.
        apps.delete(app)
      }
    }
  }
}

/**
 * A timer that keeps its period on a hidden page. The worker is the good
 * path; the plain interval is the fallback for when it cannot start at all.
 * The fallback is throttled, but the stepping above turns its coarse ticks
 * back into real time — the background round then advances in jumps rather
 * than in slow motion, which is the difference that matters.
 */
function spawnClock(onTick: () => void): () => void {
  let worker: Worker | null = null
  let timer = 0

  const useTimer = (reason: string): void => {
    if (timer) return
    log.warn('clock.worker-unavailable', 'background pacing falls back to a timer', { reason })
    timer = window.setInterval(onTick, TICK_MS)
  }

  try {
    worker = new Worker('/background-clock.js')
    worker.onmessage = onTick
    // Construction is async: a 404 or a CSP refusal surfaces here, not above.
    worker.onerror = () => {
      worker?.terminate()
      worker = null
      useTimer('worker failed to start')
    }
  } catch (err) {
    useTimer(err instanceof Error ? err.message : String(err))
  }

  return () => {
    worker?.terminate()
    worker = null
    if (timer) window.clearInterval(timer)
    timer = 0
  }
}
